// src/oddsCalculations.ts

import { RaceResult } from './types'; // Import RaceResult from the new types.ts
import { hasRaceStarted } from './utils';

/**
 * Calculates combined odds for multiple selections in the same event.
 * This function is intended for internal use within this module.
 * @param oddsArr - An array of numerical odds.
 * @returns The combined odds, or null if calculation is not possible.
 */
const calcCombinedOdds = (oddsArr: number[]): number | null => {
  if (oddsArr.length < 2) return null;
  // Sum of inverse odds (probabilities)
  const invSum = oddsArr.reduce((sum, o) => sum + (o > 0 ? 1 / o : 0), 0);
  // Combined odds is the inverse of the sum of probabilities
  return invSum > 0 ? 1 / invSum : null;
};

/**
 * Calculates the "true" odds based on lay odds for a set of race results.
 * @param resultsData - Array of race results.
 * @param racesToWinValue - The number of races required to win.
 * @returns The calculated true odds, or null.
 */
export const getTrueOddsLogic = (
  resultsData: RaceResult[],
  racesToWinValue: number | string
): number | null => {
  // Helper function to calculate probability of at least k wins from n events
  function probAtLeastKWins(probArr: number[], k: number): number {
    const n = probArr.length;
    if (k > n || k <= 0) return 0; // k must be positive and not exceed n

    let prob = 0;
    const totalCombinations = 1 << n; // 2^n

    // Iterate through all possible outcomes (represented by bitmasks)
    for (let mask = 0; mask < totalCombinations; mask++) {
      const winsInThisCombination = mask
        .toString(2)
        .split('')
        .filter((x) => x === '1').length;

      if (winsInThisCombination < k) continue; // Skip if not enough wins

      let probabilityOfThisCombination = 1;
      for (let i = 0; i < n; i++) {
        if (mask & (1 << i)) {
          // If the i-th race is a "win" in this combination
          probabilityOfThisCombination *= probArr[i];
        } else {
          // If the i-th race is a "loss" in this combination
          probabilityOfThisCombination *= 1 - probArr[i];
        }
      }
      prob += probabilityOfThisCombination;
    }
    return prob;
  }

  const usedOdds: number[] = [];
  const usedRaces = new Set<string>(); // To track unique races (event + race number)

  resultsData.forEach((row) => {
    if (hasRaceStarted(row.marketStartTime)) return; // Skip races that have started

    let raceNum = row.raceNumber;
    // Attempt to extract race number from market string if not directly available
    if (!raceNum && row.market) {
      const match = row.market.match(/R(\d+)/i);
      if (match) raceNum = match[1];
    }
    if (!raceNum) return; // Skip if race number cannot be determined

    const key = `${row.event}|${raceNum}`;
    if (usedRaces.has(key)) return; // Already processed this race

    // Find all rows for the same race (shared race)
    const sameRaceRows = resultsData.filter((r) => {
      let rn = r.raceNumber;
      if (!rn && r.market) {
        const m = r.market.match(/R(\d+)/i);
        if (m) rn = m[1];
      }
      return (
        r.event === row.event &&
        rn === raceNum &&
        rn != null &&
        !hasRaceStarted(r.marketStartTime)
      );
    });

    if (sameRaceRows.length > 1) {
      // Shared race scenario
      const lays = sameRaceRows
        .map((r) => parseFloat(r.layOdds))
        .filter((o) => !isNaN(o) && o > 0); // Ensure odds are positive
      if (lays.length > 1) {
        const combined = calcCombinedOdds(lays);
        if (combined) usedOdds.push(combined);
      } else if (lays.length === 1) {
        // If only one valid lay odd in a "shared" race context
        usedOdds.push(lays[0]);
      }
    } else if (row.layOdds) {
      // Single runner in the race for this jockey
      const layOdd = parseFloat(row.layOdds);
      if (!isNaN(layOdd) && layOdd > 0) usedOdds.push(layOdd);
    }
    usedRaces.add(key);
  });

  if (usedOdds.length === 0) return null;

  const probs = usedOdds.map((o) => 1 / o);
  let k = parseInt(String(racesToWinValue), 10);
  if (isNaN(k) || k < 1) k = 1; // Default to 1 if invalid

  if (k > usedOdds.length) return null; // Cannot win more races than available

  const overallProb = probAtLeastKWins(probs, k);
  return overallProb > 0 ? 1 / overallProb : null;
};

/**
 * Calculates the midpoint odds for a set of race results.
 * @param resultsData - Array of race results.
 * @param racesToWinValue - The number of races required to win.
 * @returns The calculated midpoint odds, or null.
 */
export const getMidpointOddsLogic = (
  resultsData: RaceResult[],
  racesToWinValue: number | string
): number | null => {
  // Helper function (can be shared if moved to a common scope)
  function probAtLeastKWins(probArr: number[], k: number): number {
    const n = probArr.length;
    if (k > n || k <= 0) return 0;
    let prob = 0;
    const totalCombinations = 1 << n;
    for (let mask = 0; mask < totalCombinations; mask++) {
      const winsInThisCombination = mask
        .toString(2)
        .split('')
        .filter((x) => x === '1').length;
      if (winsInThisCombination < k) continue;
      let probabilityOfThisCombination = 1;
      for (let i = 0; i < n; i++) {
        if (mask & (1 << i)) {
          probabilityOfThisCombination *= probArr[i];
        } else {
          probabilityOfThisCombination *= 1 - probArr[i];
        }
      }
      prob += probabilityOfThisCombination;
    }
    return prob;
  }

  const usedMidpoints: number[] = [];
  const usedRaces = new Set<string>();

  resultsData.forEach((row) => {
    if (hasRaceStarted(row.marketStartTime)) return;

    let raceNum = row.raceNumber;
    if (!raceNum && row.market) {
      const match = row.market.match(/R(\d+)/i);
      if (match) raceNum = match[1];
    }
    if (!raceNum) return;

    const key = `${row.event}|${raceNum}`;
    if (usedRaces.has(key)) return;

    const sameRaceRows = resultsData.filter((r) => {
      let rn = r.raceNumber;
      if (!rn && r.market) {
        const m = r.market.match(/R(\d+)/i);
        if (m) rn = m[1];
      }
      return (
        r.event === row.event &&
        rn === raceNum &&
        rn != null &&
        !hasRaceStarted(r.marketStartTime)
      );
    });

    if (sameRaceRows.length > 1) {
      const backOddsNum = sameRaceRows
        .map((r) => parseFloat(r.backOdds))
        .filter((o) => !isNaN(o) && o > 0);
      const layOddsNum = sameRaceRows
        .map((r) => parseFloat(r.layOdds))
        .filter((o) => !isNaN(o) && o > 0);

      if (backOddsNum.length > 0 && layOddsNum.length > 0) {
        // If multiple jockeys in same race, calculate combined back and lay, then midpoint
        const combinedBack = calcCombinedOdds(backOddsNum);
        const combinedLay = calcCombinedOdds(layOddsNum);
        if (combinedBack && combinedLay) {
          usedMidpoints.push((combinedBack + combinedLay) / 2);
        } else if (combinedBack) {
          // Fallback if only one side is calculable
          usedMidpoints.push(combinedBack);
        } else if (combinedLay) {
          usedMidpoints.push(combinedLay);
        }
      } else if (backOddsNum.length > 0) {
        // Only back odds available
        const combinedBack = calcCombinedOdds(backOddsNum);
        if (combinedBack) usedMidpoints.push(combinedBack);
      } else if (layOddsNum.length > 0) {
        // Only lay odds available
        const combinedLay = calcCombinedOdds(layOddsNum);
        if (combinedLay) usedMidpoints.push(combinedLay);
      }
    } else {
      // Single runner
      const backOdd = parseFloat(row.backOdds);
      const layOdd = parseFloat(row.layOdds);
      if (!isNaN(backOdd) && !isNaN(layOdd) && backOdd > 0 && layOdd > 0) {
        usedMidpoints.push((backOdd + layOdd) / 2);
      } else if (!isNaN(backOdd) && backOdd > 0) {
        // Fallback to back or lay if one is missing
        usedMidpoints.push(backOdd);
      } else if (!isNaN(layOdd) && layOdd > 0) {
        usedMidpoints.push(layOdd);
      }
    }
    usedRaces.add(key);
  });

  if (usedMidpoints.length === 0) return null;

  const probs = usedMidpoints.map((o) => 1 / o);
  let k = parseInt(String(racesToWinValue), 10);
  if (isNaN(k) || k < 1) k = 1;

  if (k > usedMidpoints.length) return null;

  const prob = probAtLeastKWins(probs, k);
  return prob > 0 ? 1 / prob : null;
};

/**
 * Calculates adjusted back odds by applying a percentage increase.
 * @param resultsData - Array of race results.
 * @param racesToWinValue - The number of races required to win.
 * @param backOddsPercentageValue - The percentage to increase back odds by.
 * @param applyPercentage - Whether to apply the percentage increase.
 * @returns The calculated adjusted back odds, or null.
 */
export const getAdjustedBackOddsLogic = (
  resultsData: RaceResult[],
  racesToWinValue: number | string,
  backOddsPercentageValue: number,
  applyPercentage: boolean
): number | null => {
  if (!applyPercentage || resultsData.length === 0) return null;

  // Helper function (can be shared)
  function probAtLeastKWins(probArr: number[], k: number): number {
    const n = probArr.length;
    if (k > n || k <= 0) return 0;
    let prob = 0;
    const totalCombinations = 1 << n;
    for (let mask = 0; mask < totalCombinations; mask++) {
      const winsInThisCombination = mask
        .toString(2)
        .split('')
        .filter((x) => x === '1').length;
      if (winsInThisCombination < k) continue;
      let probabilityOfThisCombination = 1;
      for (let i = 0; i < n; i++) {
        if (mask & (1 << i)) {
          probabilityOfThisCombination *= probArr[i];
        } else {
          probabilityOfThisCombination *= 1 - probArr[i];
        }
      }
      prob += probabilityOfThisCombination;
    }
    return prob;
  }

  const usedOdds: number[] = [];
  const usedRaces = new Set<string>();

  resultsData.forEach((row) => {
    if (hasRaceStarted(row.marketStartTime)) return;

    let raceNum = row.raceNumber;
    if (!raceNum && row.market) {
      const match = row.market.match(/R(\d+)/i);
      if (match) raceNum = match[1];
    }
    if (!raceNum) return;

    const key = `${row.event}|${raceNum}`;
    if (usedRaces.has(key)) return;

    const sameRaceRows = resultsData.filter((r) => {
      let rn = r.raceNumber;
      if (!rn && r.market) {
        const m = r.market.match(/R(\d+)/i);
        if (m) rn = m[1];
      }
      return (
        r.event === row.event &&
        rn === raceNum &&
        rn != null &&
        !hasRaceStarted(r.marketStartTime)
      );
    });

    if (sameRaceRows.length > 1) {
      const backs = sameRaceRows
        .map((r) => parseFloat(r.backOdds))
        .filter((o) => !isNaN(o) && o > 0);
      if (backs.length > 0) {
        // Changed from > 1 to > 0 to handle cases with single valid back odd
        const combinedBack = calcCombinedOdds(backs);
        if (combinedBack) {
          usedOdds.push(combinedBack * (1 + backOddsPercentageValue / 100));
        }
      }
    } else if (row.backOdds) {
      const backOdd = parseFloat(row.backOdds);
      if (!isNaN(backOdd) && backOdd > 0) {
        usedOdds.push(backOdd * (1 + backOddsPercentageValue / 100));
      }
    }
    usedRaces.add(key);
  });

  if (usedOdds.length === 0) return null;

  const probs = usedOdds.map((o) => 1 / o);
  let k = parseInt(String(racesToWinValue), 10);
  if (isNaN(k) || k < 1) k = 1;

  if (k > usedOdds.length) return null;

  const prob = probAtLeastKWins(probs, k);
  return prob > 0 ? 1 / prob : null;
};

/**
 * Calculates the Expected Value (EV).
 * @param trueOdds - The true odds of an event.
 * @param boostedOddsValue - The boosted odds offered.
 * @returns The calculated EV, or null.
 */
export const calculateEVLogic = (
  trueOdds: number | null,
  boostedOddsValue: string | number
): number | null => {
  if (trueOdds === null || trueOdds <= 0) return null; // True odds must be positive
  if (
    boostedOddsValue === '' ||
    boostedOddsValue === null ||
    boostedOddsValue === undefined
  )
    return null;

  const parsedBoostedOddsNum = parseFloat(String(boostedOddsValue));
  if (isNaN(parsedBoostedOddsNum) || parsedBoostedOddsNum <= 0) return null; // Boosted odds must be positive

  const trueProb = 1 / trueOdds;
  const profitIfWin = parsedBoostedOddsNum - 1; // Profit is odds minus stake (1 unit)
  const lossIfLose = -1; // Loss is the stake (1 unit)

  return trueProb * profitIfWin + (1 - trueProb) * lossIfLose;
};
